// ----- Phase phase-form-templates-ui-fixes -----
//
// Helpers for the Preview (FT-5) and Compare (FT-3/4) surfaces, which must
// show the RENDERED template body that a regulatory-affairs reader can
// understand — not raw developer output. Template bodies use `{{variable}}`
// placeholders (see migrations/..._seed_initial_document_templates.ts).

import { REGIONS, type Region } from '@/constants/regions';

// ----- phase-template-ai-directives -----
//
// Template bodies carry TWO syntaxes and they mean different things:
//   «VARIABLE»    mechanical substitution (a looked-up value is dropped in)
//   [Generate …]  an instruction to the MODEL, replaced by drafted prose at
//                 generation time.
// The preview does not run the model, so a directive must never be printed as
// if it were letter text — the reader would take authoring instructions for
// content. Mirrors `src/lib/templates/directives.ts` on the server; the sample-
// mode Text tab renders here on the client, which is where Sharon saw them.

/** Matches one `[Generate …]` directive, tolerating one level of nesting. */
export const DIRECTIVE_PATTERN = /\[Generate\b(?:[^[\]]|\[[^\]]*\])*\]/g;

/** Shown wherever a directive is displayed rather than executed. */
export const DIRECTIVE_PLACEHOLDER_LABEL = 'AI-drafted section — generated at submission time';

/** How many AI directives this body carries. */
export function countTemplateDirectives(body: string): number {
  return (body.match(DIRECTIVE_PATTERN) ?? []).length;
}

/**
 * Render a template body for human reading: each `{{variable}}` placeholder
 * becomes a readable `[VARIABLE]` token so the reader sees where values get
 * substituted without the developer-facing `{{…}}` syntax, and each
 * `[Generate …]` directive becomes a clearly-marked AI-drafted placeholder.
 */
export function renderTemplatePreview(body: string): string {
  return body
    .replace(DIRECTIVE_PATTERN, `«${DIRECTIVE_PLACEHOLDER_LABEL}»`)
    .replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_m, name: string) => `[${name.toUpperCase()}]`);
}

// Region tokens that may appear in a template name when the structured
// Region column is null (e.g. "FDA Type D Meeting Request").
const NAME_REGION_PATTERNS: ReadonlyArray<readonly [RegExp, Region]> = [
  [/\bFDA\b/i, 'FDA'],
  [/\bEMA\b/i, 'EMA'],
  [/\bMHRA\b/i, 'MHRA'],
  [/health\s*canada|\bHC\b|\bNDS\b/i, 'Health Canada'],
  [/\bPMDA\b/i, 'PMDA'],
  [/\bTGA\b/i, 'TGA'],
  [/swissmedic/i, 'Swissmedic'],
];

export type DiffLineType = 'equal' | 'added' | 'removed';
export interface DiffLine {
  readonly text: string;
  readonly type: DiffLineType;
}
export interface LineDiff {
  /** Left column: 'equal' or 'removed' lines (in order). */
  readonly left: DiffLine[];
  /** Right column: 'equal' or 'added' lines (in order). */
  readonly right: DiffLine[];
}

/**
 * Phase phase-form-templates-ui-fixes (FT-3/4) — a simple line-level diff for
 * the side-by-side Compare view. Uses a longest-common-subsequence over lines
 * so unchanged lines align and changed/added/removed lines are highlightable.
 */
export function lineDiff(aText: string, bText: string): LineDiff {
  const a = aText.split('\n');
  const b = bText.split('\n');
  const n = a.length;
  const m = b.length;
  // LCS length table.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const left: DiffLine[] = [];
  const right: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      left.push({ text: a[i]!, type: 'equal' });
      right.push({ text: b[j]!, type: 'equal' });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      left.push({ text: a[i]!, type: 'removed' });
      i++;
    } else {
      right.push({ text: b[j]!, type: 'added' });
      j++;
    }
  }
  while (i < n) {
    left.push({ text: a[i]!, type: 'removed' });
    i++;
  }
  while (j < m) {
    right.push({ text: b[j]!, type: 'added' });
    j++;
  }
  return { left, right };
}

/**
 * Best-effort region for a template: prefer the structured Region column,
 * falling back to extracting a region token from the template name when the
 * column is null/empty. Returns null when neither yields a known region.
 */
export function regionOfTemplate(t: { Region?: string | null; Name: string }): Region | null {
  const structured = (t.Region ?? '').trim();
  if (structured) {
    const match = REGIONS.find((r) => r.toLowerCase() === structured.toLowerCase());
    if (match) return match;
  }
  for (const [pattern, region] of NAME_REGION_PATTERNS) {
    if (pattern.test(t.Name)) return region;
  }
  return null;
}
